import styled, { keyframes, css } from 'styled-components';
import { Button } from './Core';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import SpinnerButton from './SpinnerButton';
import useScreenSize from '../../hooks/useScreenSize';
import useTranslation from '../../hooks/useTranslation';
import { Icon_Plus, resolveIcon } from '../../styles/icons';

const darkFlash = keyframes`
  0%, 100% { filter: brightness(1); }
  50% { filter: brightness(0.6); }
`;

const clickFlash = keyframes`
  0%, 100% { filter: brightness(1); }
  50% { filter: brightness(1.3); }
`;

const MobileWrapper = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  margin-left: 0.5rem;
`;

const CircleButton = styled(Button)`
  width: 2rem;
  height: 2rem;
  margin-right: 0rem;
  padding: 0;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }

  ${props => props.$flash && css`
    svg {
      animation: ${darkFlash} 0.6s ease-in-out;
      animation-iteration-count: 4; 
  `}

  ${props => props.$cooldown && css`
    opacity: 0.6;
    cursor: not-allowed;
    animation: ${clickFlash} 0.3s ease-in-out infinite;
  `}
`;

/*
 * The compact label sits outside the coloured circle, on whatever surface hosts
 * the button. Inside an Alert that is the banner, so it takes the banner's own
 * --alert-color, which cascades from the Alert wrapper and is already dark-aware.
 * Anywhere else the variable is undefined and the ordinary font colour applies.
 * This was a hardcoded black keyed off $brightBackground, which only read while
 * every alert surface was pale — on a dark banner it left the label invisible.
 */
const MobileLabel = styled.div`
  text-align: center;
  font-size: var(--font-XS);
  margin-top: 0.25rem;
  color: var(--alert-color, ${({ theme }) => theme.font.color});
`;

const RButtonWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  width: ${({ $width }) => ($width ? $width : 'auto')};
  min-width: 0;
`;

const DesktopLabelNoWrap = styled.span`
  white-space: nowrap;
`;

const MeasureButton = styled(Button)`
  position: absolute;
  left: -9999px;
  top: 0;
  visibility: hidden;
  pointer-events: none;
  margin-right: 0;
  width: 100%;
  display: inline-flex;
  align-items: center;
`;

const MeasureLabel = styled(DesktopLabelNoWrap)`
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
`;

const MeasureSpinnerPad = styled.span`
  display: inline-block;
  width: 2rem;
`;

const DesktopButtonWrapper = styled.div`
  display: inline-flex;
  ${props => props.$cooldown && css`
    animation: ${clickFlash} 0.3s ease-in-out infinite;
  `}
`;

/**
 * Responsive Button (RButton)
 *
 * Props:
 * - labelKey: i18n key used with useTranslation to render the label
 * - onClick: click handler
 * - spinner: boolean, if true uses SpinnerButton on desktop and flashes the mobile icon for 2s
 * - icon: optional React element to render inside the mobile round button (defaults to Icon_Plus)
 * - $variant, $shadow, width, disabled, name, type, testId: forwarded to underlying button
 */
const RButton = ({
  label,
  onClick,
  spinner = false,
  icon,
  $variant = 'primary',
  $shadow = true,
  width,
  disabled,
  name,
  type,
  testId,
  preferCompactOnMobile = true,
  onVariantChange,
  $brightBackground,
  $marginRight
}) => {
  const { isMobile } = useScreenSize();
  const { t } = useTranslation();

  const buttonLabel = label ? t(label) : '';
  const IconEl = resolveIcon(icon) ?? Icon_Plus;

  const initialIsCompact = preferCompactOnMobile && isMobile;
  const [measured, setMeasured] = useState(initialIsCompact);
  const [variant, setVariant] = useState(initialIsCompact ? 'compact' : 'desktop');

  const wrapperRef = useRef(null);
  const measureLabelRef = useRef(null);
  const measureButtonRef = useRef(null);
  const rafRef = useRef(null);

  const wasMobileRef = useRef(preferCompactOnMobile && isMobile);

  const [flashing, setFlashing] = useState(false);
  const timeoutRef = useRef(null);
  const clickTsRef = useRef(0);

  // Click lock state for preventing double clicks
  const [clickLocked, setClickLocked] = useState(false);
  const clickLockTimeoutRef = useRef(null);

  // Cleanup any pending timers on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (clickLockTimeoutRef.current) clearTimeout(clickLockTimeoutRef.current);
    };
  }, []);
  useEffect(() => {
    if (spinner) {
      const now = Date.now();
      // Only start flashing from spinner if it was triggered by a recent click on this button
      if (now - clickTsRef.current <= 800) {
        setFlashing(true);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => setFlashing(false), 2000);
      }
    }
  }, [spinner]);

  const measureAndSelectVariant = () => {
    if (preferCompactOnMobile && isMobile) {
      wasMobileRef.current = true;
      if (!measured || variant !== 'compact') {
        setMeasured(true);
        setVariant('compact');
        if (onVariantChange) onVariantChange('compact');
      }
      return;
    }

    // Mobile→desktop transition: parent container is still sized to compact buttons,
    // so free-space estimation returns 0 and measurement gets stuck in compact.
    // Force desktop once to break the deadlock; ResizeObserver re-measures after layout settles.
    if (wasMobileRef.current) {
      wasMobileRef.current = false;
      if (!measured || variant !== 'desktop') {
        setMeasured(true);
        setVariant('desktop');
        if (onVariantChange) onVariantChange('desktop');
      }
      return;
    }

    // If there is no label, default to desktop to preserve existing semantics.
    if (!buttonLabel) {
      if (!measured || variant !== 'desktop') {
        setMeasured(true);
        setVariant('desktop');
        if (onVariantChange) onVariantChange('desktop');
      }
      return;
    }

    const wrapperEl = wrapperRef.current;
    const labelEl = measureLabelRef.current;
    const measureBtnEl = measureButtonRef.current;

    // If we can't measure yet, fail open to desktop (avoids hiding the button indefinitely).
    if (!wrapperEl || !labelEl || !measureBtnEl) {
      if (!measured || variant !== 'desktop') {
        setMeasured(true);
        setVariant('desktop');
        if (onVariantChange) onVariantChange('desktop');
      }
      return;
    }

    const wrapperWidth = wrapperEl.getBoundingClientRect().width;
    if (!wrapperWidth || wrapperWidth <= 0) {
      if (!measured || variant !== 'desktop') {
        setMeasured(true);
        setVariant('desktop');
        if (onVariantChange) onVariantChange('desktop');
      }
      return;
    }

    // When we're already compact, the wrapper can remain small even if the surrounding layout expands.
    // Estimate a "potential" width using any free space available in the parent container.
    let availableWidth = wrapperWidth;
    const parentEl = wrapperEl.parentElement;
    if (parentEl) {
      const parentWidth = parentEl.getBoundingClientRect().width;
      if (parentWidth > 0) {
        let sumChildren = 0;
        // NOTE: this includes wrapperEl itself. That's intended so freeSpace reflects *current* layout.
        for (const child of parentEl.children) {
          const r = child.getBoundingClientRect();
          sumChildren += r.width;
        }
        const freeSpace = parentWidth - sumChildren;
        availableWidth = wrapperWidth + Math.max(0, freeSpace);
      }
    }

    // Constrain the measurer to the available width, then detect overflow.
    measureBtnEl.style.width = `${availableWidth}px`;

    const fits = labelEl.scrollWidth <= labelEl.clientWidth;
    const nextVariant = fits ? 'desktop' : 'compact';

    if (!measured || variant !== nextVariant) {
      setMeasured(true);
      setVariant(nextVariant);
      if (onVariantChange) onVariantChange(nextVariant);
    }
  };

  const scheduleMeasure = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      measureAndSelectVariant();
    });
  };

  useLayoutEffect(() => {
    // Synchronous measurement path (before paint) to avoid flicker.
    measureAndSelectVariant();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buttonLabel, spinner, isMobile, preferCompactOnMobile, width]);

  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  useEffect(() => {
    // Observe container size changes.
    if (preferCompactOnMobile && isMobile) return;
    const el = wrapperRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const parentEl = el.parentElement;

    const ro = new ResizeObserver(() => scheduleMeasure());
    ro.observe(el);
    if (parentEl) ro.observe(parentEl);

    return () => {
      ro.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, preferCompactOnMobile]);

  useEffect(() => {
    // Re-measure after fonts load (if supported).
    if (preferCompactOnMobile && isMobile) return;
    let cancelled = false;
    const fonts = document?.fonts;
    if (!fonts?.ready) return;

    fonts.ready
      .then(() => {
        if (!cancelled) scheduleMeasure();
      })
      .catch(() => {
        // ignore
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buttonLabel, isMobile, preferCompactOnMobile]);

  // Cooldown controller for preventing double clicks
  const triggerClickLock = () => {
    setClickLocked(true);

    if (clickLockTimeoutRef.current) {
      clearTimeout(clickLockTimeoutRef.current);
    }

    clickLockTimeoutRef.current = setTimeout(() => {
      setClickLocked(false);
    }, 3000);
  };

  // Desktop click handler with cooldown protection
  const handleDesktopClick = (e) => {
    if (clickLocked || disabled) return;

    triggerClickLock();

    if (onClick) onClick(e);
  };

  // Start flashing immediately on mobile click, even before parent toggles spinner
  const handleMobileClick = (e) => {
    if (clickLocked || disabled) return;

    triggerClickLock();

    clickTsRef.current = Date.now();
    setFlashing(true);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      if (!spinner) setFlashing(false);
    }, 2000);
    if (onClick) onClick(e);
  };

  const desktopNode = (
    <DesktopButtonWrapper $cooldown={clickLocked}>
      {spinner ? (
        <SpinnerButton
          type={type}
          name={name}
          $shadow={$shadow}
          $brightBackground={$brightBackground}
          width={width}
          $variant={$variant}
          disabled={disabled || clickLocked}
          onClickEvent={handleDesktopClick}
          testId={testId}
          $marginRight={$marginRight}
        >
          <DesktopLabelNoWrap>{buttonLabel}</DesktopLabelNoWrap>
        </SpinnerButton>
      ) : (
        <Button
          type={type}
          name={name}
          $shadow={$shadow}
          $brightBackground={$brightBackground}
          width={width}
          $variant={$variant}
          disabled={disabled || clickLocked}
          onClick={handleDesktopClick}
          data-testid={testId}
          $marginRight={$marginRight}
        >
          <DesktopLabelNoWrap>{buttonLabel}</DesktopLabelNoWrap>
        </Button>
      )}
    </DesktopButtonWrapper>
  );

  const compactNode = (
    <MobileWrapper>
      <CircleButton
        type={type}
        name={name}
        $shadow={$shadow}
        $brightBackground={$brightBackground}
        $variant={$variant}
        $flash={flashing}
        $cooldown={clickLocked}
        disabled={disabled || clickLocked}
        onClick={handleMobileClick}
        data-testid={testId}
        aria-label={buttonLabel}
      >
        {IconEl}
      </CircleButton>
      {buttonLabel && <MobileLabel>{buttonLabel}</MobileLabel>}
    </MobileWrapper>
  );

  return (
    <RButtonWrapper ref={wrapperRef} $width={width} data-variant={measured ? variant : 'unmeasured'}>
      {/* Hidden measurer: constrains label to wrapper width and checks for overflow */}
      {(!preferCompactOnMobile || !isMobile) && (
        <MeasureButton ref={measureButtonRef} type="button" $shadow={$shadow} $variant={$variant} disabled={disabled || clickLocked} aria-hidden="true" tabIndex={-1}>
          <MeasureLabel ref={measureLabelRef}>{buttonLabel}</MeasureLabel>
          {spinner && <MeasureSpinnerPad />}
        </MeasureButton>
      )}

      {/* No-flicker rule: do not render a visible variant until measured */}
      {measured ? (variant === 'desktop' ? desktopNode : compactNode) : null}
    </RButtonWrapper>
  );
};

export default RButton;
